import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Card,
  CardMedia,
  Box,
  IconButton,
  Container,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import RightPreview from "./RightPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage as farImage } from "@fortawesome/free-regular-svg-icons";
import { faVideo as fasVideo, faHome as fasHome, faFilePdf as fasPdf } from "@fortawesome/free-solid-svg-icons";
import { getFolder } from "../../api/services";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import PdfView from "./PdfView";

const ITEMS_PER_PAGE = 8;

type File = {
  id: string;
  name: string;
  fileType: "image" | "video" | "document";
  url: string;
  size: number;
  createdAt: string; // Assuming files have timestamps for sorting
};

type Folder = {
  id: string;
  title: string;
  files: File[];
};

const SharableFolder: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [sortOrder, setSortOrder] = useState<"latest" | "oldest">("latest"); // Sorting state
  // const [hidename, setHidename] = useState(false);

  const navigate = useNavigate();

  const { data: folderData, isLoading, isError } = useQuery(["folderData", id], () => getFolder(id || ""), {
    enabled: !!id,
  });

  const folder = folderData?.data;


  // useEffect(() => {
  //   if( folderData?.data?._id === "676fd57a5f2e680edf8554a4" || folderData?.data?._id === "676fd56c5f2e680edf8554a1" || folderData?.data?._id === "676fd5545f2e680edf85549e" ){
  //     setHidename(true);
  //   }
  // }, [folderData]); 




  if (isLoading) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <LinearProgress />
      </Box>
    );
  }

  if (isError || !folder) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Typography variant="h4" color="error">
          Folder Not Found
        </Typography>
      </Box>
    );
  }




  const handleSelectFile = (index: number) => {
    setSelectedFileIndex(index);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedFileIndex(null);
  };

  const handleSortChange = (event: any) => {
    setSortOrder(event.target.value);
  };

  const handleNextFile = () => {
    if (selectedFileIndex !== null && sortedFiles) {
      const nextIndex = (selectedFileIndex + 1) % sortedFiles.length;
      setSelectedFileIndex(nextIndex);
      // Scroll the selected item into view
      const element = document.getElementById(`file-item-${nextIndex}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handlePreviousFile = () => {
    if (selectedFileIndex !== null && sortedFiles) {
      const previousIndex = selectedFileIndex === 0 ? sortedFiles.length - 1 : selectedFileIndex - 1;
      setSelectedFileIndex(previousIndex);
      // Scroll the selected item into view
      const element = document.getElementById(`file-item-${previousIndex}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Sorting the files based on selected order
  const sortedFiles = [...folder.filesdata].sort((a, b) => {
    return sortOrder === "latest"
      ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", padding: 2 }}>
          <Box sx={{ flex: 1, paddingRight: isPreviewOpen ? 4 : 0, width: isPreviewOpen ? "50%" : "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <Breadcrumbs separator="/">
                <IconButton size="small" onClick={() => navigate("/folders")}>
                  <FontAwesomeIcon icon={fasHome} />
                </IconButton>
                <MuiLink component={Link} to="/folders" underline="hover">
                  All Folders
                </MuiLink>
                <MuiLink component={Link} to={`/folders/${folder.id}`} underline="hover">
                    {folder.name.length > 10 ? `${folder.name.substring(0, 10)}...` : folder.name}
                </MuiLink>
              </Breadcrumbs>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="subtitle1">{folder.files?.length} Files</Typography>

                {/* Sorting Dropdown */}
                {/* <FormControl size="small" sx={{ minWidth: 'auto' }}>
                  <Select
                        value={sortOrder}
                        onChange={handleSortChange}
                        displayEmpty
                        sx={{
                          backgroundColor: "secondary.main",
                          color: "#ffffff",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "&:hover": { backgroundColor: "#3a3a3a" },
                          height: "30px",
                          width : "auto"
                        }}
                      >
                        <MenuItem value="latest">Latest</MenuItem>
                        <MenuItem value="oldest">Oldest</MenuItem>
                      </Select>
                    </FormControl> */}

              </Box>
            </Box>

            <Typography variant="h3" gutterBottom>
              {folder.title}
            </Typography>

            <Grid container spacing={3}>
              {sortedFiles.slice(0, visibleCount).map((file: File, index: number) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={file.id} id={`file-item-${index}`}>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => handleSelectFile(index)}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border: selectedFileIndex === index ? "2px solid #fc0000" : "1px solid #454442",
                        padding: "1px",
                        height: "100%",
                        borderRadius: "0px",
                        transition: "border 0.3s ease",
                        transform: selectedFileIndex === index ? 'scale(1.02)' : 'scale(1)',
                        '&:hover': {
                          borderColor: selectedFileIndex === index ? "#fc0000" : "#666",
                        }
                      }}
                    >
                      <Box sx={{ p: 3, backgroundColor: "#454442" }}>
                        {file.fileType === "image" && (
                          <CardMedia
                            component="img"
                            image={`https://dprstorage.b-cdn.net${file.url}`}
                            alt={file.name}
                            sx={{ height: 150, objectFit: "contain", display: "block", margin: "auto", borderRadius: "0px" }}
                          />
                        )}
                        {file.fileType === "video" && (
                          <video
                            src={`https://dprstorage.b-cdn.net${file.url}`}
                            style={{ width: "100%", height: "150px", objectFit: "contain", borderRadius: "0px" }}
                            muted
                            loop
                          />
                        )}
                        {file.fileType === "document" && (
                          <iframe
                            src={`https://dprstorage.b-cdn.net${file.url}`}
                            style={{ width: "100%", height: "150px", objectFit: "contain", borderRadius: "0px" }}
                            onClick={() => handleSelectFile(index)}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", p: 1, backgroundColor: "#000000" }}>
                        <Box sx={{ p: 1, display: "flex", alignItems: "center" }}>
                          {file.fileType === "image" && <FontAwesomeIcon icon={farImage} style={{ color: "#ffffff" }} />}
                          {file.fileType === "video" && <FontAwesomeIcon icon={fasVideo} />}
                          {file.fileType === "document" && <FontAwesomeIcon icon={fasPdf} />}
                        </Box>
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="subtitle1" noWrap>
                            {file.name.length > 25 ? `${file.name.substring(0, 24)}...` : file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                          {
                            file.size > 1024 * 1024 ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / 1024).toFixed(2)} KB
                              </Typography>
                            )
                          }   
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {visibleCount < folder.files.length && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Button variant="contained" onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}>
                  Show More
                </Button>
              </Box>
            )}
          </Box>
          {isPreviewOpen && selectedFileIndex !== null && sortedFiles[selectedFileIndex].fileType !== "document" && (
            <RightPreview 
              file={sortedFiles[selectedFileIndex]} 
              onClose={handleClosePreview}
              onNext={handleNextFile}
              onPrevious={handlePreviousFile}
            />
          )}
          {isPreviewOpen && selectedFileIndex !== null && sortedFiles[selectedFileIndex].fileType === "document" && (
            <PdfView 
              fileUrl={`https://dprstorage.b-cdn.net${sortedFiles[selectedFileIndex].url}`} 
              onClose={handleClosePreview}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SharableFolder;
